import React, { useState } from 'react';
import { Layout, Menu, Button, Divider, Avatar } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import { useResponsive } from '../../hooks/useResponsive';
import BottomNavBar from './BottomNavBar';
import TopNavBar from './TopNavBar';

const { Sider, Content } = Layout;

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = React.memo(({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);
  const { isMobile } = useResponsive();

  console.log('isMobile:', isMobile);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <i className="fas fa-tachometer-alt" />, label: 'Dashboard',
    },
    {
      key: '/medicines',
      icon: <i className="fas fa-pills" />, label: 'Quản lý thuốc',
    },
    {
      key: '/inventory',
      icon: <i className="fas fa-boxes" />, label: 'Tồn kho',
    },
    {
      key: '/suppliers',
      icon: <i className="fas fa-truck" />, label: 'Nhà cung cấp',
    },
    {
      key: '/customers',
      icon: <i className="fas fa-users" />, label: 'Khách hàng',
    },
    {
      key: '/stock-in',
      icon: <i className="fas fa-arrow-down" />, label: 'Nhập kho',
    },
    {
      key: '/stock-out',
      icon: <i className="fas fa-arrow-up" />, label: 'Xuất kho',
    },
    {
      key: '/debts',
      icon: <i className="fas fa-dollar-sign" />, label: 'Công nợ',
    },
    {
      key: '/expenses',
      icon: <i className="fas fa-receipt" />, label: 'Quản lý chi phí',
    },
    {
      key: '/reports',
      icon: <i className="fas fa-chart-bar" />,
      label: 'Báo cáo',
      children: [
        { key: '/reports/revenue', label: 'Báo cáo kinh doanh' },
        { key: '/reports/stock-in-out', label: 'Báo cáo nhập-xuất-tồn' },
        { key: '/reports/profit', label: 'Báo cáo lợi nhuận' },
      ],
    },
    {
      key: '/system',
      icon: <i className="fas fa-cog" />, label: 'Thông tin hệ thống',
    },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Xác định tiêu đề động cho mobile top bar
  const getMobileTitle = () => {
    const path = location.pathname;
    if (path.startsWith('/dashboard')) return 'Dashboard';
    if (path.startsWith('/medicines')) return 'Quản lý thuốc';
    if (path.startsWith('/inventory')) return 'Tồn kho';
    if (path.startsWith('/suppliers')) return 'Nhà cung cấp';
    if (path.startsWith('/customers')) return 'Khách hàng';
    if (path.startsWith('/stock-in')) return 'Nhập kho';
    if (path.startsWith('/stock-out')) return 'Xuất kho';
    if (path.startsWith('/debts')) return 'Công nợ';
    if (path.startsWith('/expenses')) {
      return 'Quản lý chi phí';
    }
    if (path.startsWith('/reports')) return 'Báo cáo';
    if (path.startsWith('/system')) return 'Thông tin hệ thống';
    return '';
  };

  return (
    <Layout className="app-layout-root" style={{ minHeight: '100vh', position: 'relative' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          width={250}
          theme="dark"
          collapsed={collapsed}
          onCollapse={setCollapsed}
          style={{
            position: 'fixed',
            height: '100vh',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 1000,
            transition: 'all 0.3s ease',
            overflowX: 'hidden',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        >
          {/* User Avatar Section */}
          <div className="logo" style={{ 
            textAlign: 'center', 
            padding: '16px',
            position: 'relative'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'space-between',
              gap: '12px'
            }}>
              {/* Avatar */}
              <Avatar
                size={collapsed ? 32 : 40}
                src={user?.avatar}
                style={{
                  backgroundColor: user?.avatar ? 'transparent' : '#1890ff',
                  border: '2px solid #fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
              >
                {!user?.avatar && (user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U')}
              </Avatar>
              
              {/* User Info - Only show when not collapsed */}
              {!collapsed && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  flex: 1,
                  minWidth: 0,
                  marginLeft: 8
                }}>
                  <div style={{
                    fontWeight: 700,
                    fontSize: 18,
                    color: '#fff',
                    marginBottom: 4,
                    textAlign: 'left',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>{user?.full_name || user?.username}</div>
                  <div style={{
                    fontSize: 14,
                    color: '#fff',
                    fontWeight: 500,
                    marginBottom: 2,
                    textAlign: 'left',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>{user?.username || 'Người dùng'}</div>
                </div>
              )}
            </div>
          </div>

          {/* Divider between Logo and Menu */}
          <Divider style={{ margin: '0 16px', borderColor: '#303030' }} />

          {/* Menu Section */}
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
            style={{ 
              height: 'calc(100vh - 200px)', 
              overflowY: 'auto',
              overflowX: 'hidden',
              textAlign: 'left'
            }}
            className="sidebar-menu"
          />

          {/* User Action Section */}
          <div style={{ 
            position: 'absolute', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            padding: '16px',
            background: '#001529',
            borderTop: '1px solid #303030'
          }}>
            {/* User Action Buttons */}
            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
              {/* Logout Button */}
              <Button 
                type="text" 
                icon={<i className="fas fa-sign-out-alt" />}
                onClick={handleLogout}
                style={{ 
                  flex: 1,
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: '#ff4d4f',
                  background: 'transparent',
                  border: '1px solid #303030',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  height: '36px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 77, 79, 0.1)';
                  e.currentTarget.style.borderColor = '#ff4d4f';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#303030';
                }}
              >
                <span style={{ fontSize: '13px', marginLeft: '8px' }}>Đăng xuất</span>
              </Button>
            </div>
          </div>
        </Sider>
      )}

      {/* Toggle Sidebar Button - Desktop only */}
      {!isMobile && (
        <Button 
          type="text" 
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => setCollapsed(!collapsed)}
          style={{ 
            position: 'fixed',
            top: '24px',
            left: collapsed ? '60px' : '230px',
            width: '40px',
            height: '40px',
            color: '#8c8c8c',
            background: '#fff',
            border: '1px solid #d9d9d9',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f0f0f0';
            e.currentTarget.style.borderColor = '#1890ff';
            e.currentTarget.style.color = '#1890ff';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#fff';
            e.currentTarget.style.borderColor = '#d9d9d9';
            e.currentTarget.style.color = '#8c8c8c';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
          }}
        />
      )}

      {/* Main Content */}
      <Layout style={{ 
        marginLeft: !isMobile ? (collapsed ? 80 : 250) : 0,
        paddingBottom: isMobile ? '80px' : 0,
        height: '100vh',
        overflow: 'hidden'
      }}>
        {isMobile && <TopNavBar title={getMobileTitle()} />}
        <Content style={{ 
          margin: isMobile ? '16px' : '24px',
          background: '#f0f2f5',
          borderRadius: '8px',
          height: '100%',
          overflow: 'auto',
          position: 'relative',
          paddingBottom: isMobile ? '80px' : '24px',
          paddingTop: isMobile ? 52 : 0 // Để không bị TopNavBar che
        }}>
          {children}
        </Content>
      </Layout>

      {/* Mobile Bottom Navigation */}
      {isMobile && <BottomNavBar />}
    </Layout>
  );
});

AppLayout.displayName = 'AppLayout';

export default AppLayout; 